import { useEffect, useRef, useState } from 'preact/hooks';
import { Header } from './components/Header';
import { ChoiceScreen } from './components/ChoiceScreen';
import { Message } from './components/Message';
import { Composer } from './components/Composer';
import { Sparkle } from './components/Sparkle';
import { useChat, UseChatCallbacks } from './state/useChat';
import type { AskJuminoConfig } from './config';

/** Populated by App (via useChat) while mounted so the custom element's
 * imperative newConversation()/close() can reach into live chat state without
 * a global store. See element.ts. */
export interface ChatBridge {
  startNew?: () => void;
  teardown?: () => void;
}

export interface AppProps {
  config: AskJuminoConfig;
  onClose: () => void;
  chatCallbacks?: UseChatCallbacks;
  bridge?: ChatBridge;
  /** Notified on every fullscreen toggle so the custom element can mirror the
   * state onto its own :host attribute — see element.ts's setFullscreen(). */
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

/**
 * Root of the widget's rendered tree — the Preact equivalent of the drawer's
 * .html + .component.ts pair. Ported behavior: a 'choice' screen when a
 * resumable conversation exists, otherwise straight into 'chat'; an error
 * banner; an empty state; the message list; and the composer + footer.
 */
export function App({ config, onClose, chatCallbacks, bridge, onFullscreenChange }: AppProps) {
  const chat = useChat(config, chatCallbacks);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = () => {
    const next = !isFullscreen;
    setIsFullscreen(next);
    onFullscreenChange?.(next);
  };

  // Mirror onto the bridge object the custom element holds — see element.ts.
  if (bridge) {
    bridge.startNew = chat.startNew;
    bridge.teardown = chat.teardown;
  }
  const title = config.strings?.title ?? 'Ask Jumino';
  const placeholder = config.strings?.placeholder ?? 'Ask anything to do with Nojumi!';
  const emptyText = config.strings?.empty ?? 'How can I help you?';

  // A reply taller than the body scrolls to its own top, not the bottom.
  const bodyRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(0);
  const prevViewRef = useRef(chat.view);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;

    const hasNewMessage = chat.messages.length > prevMessageCountRef.current;
    const viewJustRestored = prevViewRef.current !== 'chat' && chat.view === 'chat';
    const lastMessage = chat.messages[chat.messages.length - 1];
    prevMessageCountRef.current = chat.messages.length;
    prevViewRef.current = chat.view;

    const id = setTimeout(() => {
      if (hasNewMessage && !viewJustRestored && lastMessage?.role === 'assistant') {
        const msgEls = el.querySelectorAll('.aj-msg');
        const lastMsgEl = msgEls[msgEls.length - 1] as HTMLElement | undefined;
        if (lastMsgEl && lastMsgEl.getBoundingClientRect().height > el.clientHeight) {
          // Matches .aj-drawer__body's own padding, so the message rests at
          // the same visual offset a top-of-list item normally would.
          const TOP_GAP = 24;
          const delta =
            lastMsgEl.getBoundingClientRect().top - el.getBoundingClientRect().top - TOP_GAP;
          el.scrollTop += delta;
          return;
        }
      }
      el.scrollTop = el.scrollHeight;
    });
    return () => clearTimeout(id);
  }, [chat.messages.length, chat.isLoading, chat.view]);

  const close = () => {
    chat.teardown();
    onClose();
  };

  // Escape closes the panel, or exits fullscreen first if that's active.
  const escapeActionRef = useRef<() => void>(close);
  escapeActionRef.current = isFullscreen ? toggleFullscreen : close;
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        escapeActionRef.current();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div class="aj-drawer">
      <Header
        title={title}
        onClose={close}
        // Fullscreen toggle only makes sense for layout="floating".
        isFullscreen={config.layout === 'floating' ? isFullscreen : undefined}
        onToggleFullscreen={config.layout === 'floating' ? toggleFullscreen : undefined}
      />

      {chat.view === 'choice' ? (
        <ChoiceScreen onContinue={chat.continuePrevious} onStartNew={chat.startNew} />
      ) : (
        <>
          {chat.errorMessage && (
            /* A rate limit is a wait, not a fault, so it reads as a notice
               rather than a failure — the user did nothing wrong. */
            <div class={`aj-drawer__error${chat.cooldownUntil ? ' aj-drawer__error--notice' : ''}`}>
              <span>
                {chat.cooldownUntil ? '⏳' : '⚠️'} {chat.errorMessage}
              </span>
              <button type="button" class="aj-drawer__error-dismiss" onClick={chat.dismissError}>
                ✕
              </button>
            </div>
          )}

          {/* role="log" + aria-live so screen readers announce new turns as they
              arrive — the Angular drawer never had this; closing the gap here. */}
          <div class="aj-drawer__body" role="log" aria-live="polite" ref={bodyRef}>
            {chat.messages.length === 0 && !chat.isLoading && (
              <div class="aj-drawer__empty">
                <span class="aj-drawer__empty-icon">
                  <Sparkle />
                </span>
                {emptyText}
              </div>
            )}

            {chat.messages.map((message, i) => (
              <Message key={i} message={message} />
            ))}

            {chat.isLoading && (
              <div class="aj-msg aj-msg--assistant aj-msg--loading">
                <div class="aj-msg__content">
                  <span>Thinking…</span>
                  <span class="aj-spinner" />
                </div>
              </div>
            )}
          </div>

          <Composer
            disabled={!chat.sessionId}
            isLoading={chat.isLoading}
            placeholder={placeholder}
            onSend={chat.sendMessage}
            cooldownUntil={chat.cooldownUntil}
          />

          <div class="aj-drawer__footer">
            <button type="button" class="aj-drawer__footer-btn" onClick={close}>
              Close
            </button>
          </div>
        </>
      )}
    </div>
  );
}
